import type { DataFlowBuilder } from '@/engine/DataFlow';
import * as DT from './TTVRDataTypes';
import * as Algo from './TTVRAlgorithmHelpers';
import { buildPointToPlaneDistanceMeasurementSigned } from '../common/CardiacAlgorithmBuilderCommon';

export function buildPapillaryAlgorithm(builder: DataFlowBuilder, descriptor: string): void {
  const importedPoint = builder.name('pt_' + descriptor + '_imported').declare(DT.Point);

  builder
    .name('tp.pt_' + descriptor)
    .bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.name('tp.pt_' + descriptor + '.dflt').bindRaw(Algo.reference, DT.Point, importedPoint),
      builder.name('tp.pt_' + descriptor + '.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_' + descriptor + '.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_' + descriptor + '.flag').define(Algo.defaultTuneFlag())
    );

  builder
    .name(descriptor + '.quarter')
    .bind(Algo.findAnnulusQuarterIndexTTVR, DT.NumberT, 'tp.pt_' + descriptor, 'landing_zone_area', 'tp.ap_direction');

  const measInfo = buildPointToPlaneDistanceMeasurementSigned(builder, 'tp.pt_' + descriptor, 'pl_annulus');

  builder
    .name('var_' + descriptor + '_height')
    .bindRaw(
      Algo.makeDistanceMeasurement,
      DT.DistanceMeasurement,
      measInfo.distance,
      measInfo.line,
      builder.bindRaw(
        Algo.makeRoiForHorizontalLine,
        DT.RegionOfInterest,
        measInfo.line,
        builder.existing('world_top_direction'),
        builder.existing('world_anterior_direction')
      )
    );
}

export function buildPapillaryCommonAlgorithm(builder: DataFlowBuilder, descriptors: Array<string>): void {
  builder.name('available_papillary').define(new DT.IdentifiersList(descriptors));

  const papillaryDistributionInfo = builder
    .name('var_papillary_distribution_value')
    .bind(Algo.countUniquePapillaryQuartersTTVR, DT.NumberT, ...descriptors.map((d) => d + '.quarter'));

  builder
    .name('var_papillary_distribution')
    .bindRaw(
      Algo.makeValueMeasurement,
      DT.ValueMeasurement,
      papillaryDistributionInfo,
      builder.bindRaw(
        Algo.makeYesMaybeCriteria,
        DT.CheckCriteria,
        papillaryDistributionInfo,
        builder.bindRaw(Algo.makeGreaterThanCriteriaRange, DT.CriteriaRange, builder.define(new DT.NumberT(2)))
      )
    );

  const papillaryHeightsInfo = builder
    .name('var_papillary_heights_value')
    .bindRaw(
      Algo.countPapillaryHeightsTTVR,
      DT.NumberT,
      builder.existing('landing_zone_area'),
      builder.define(new DT.NumberT(25)),
      ...descriptors.map((d) => builder.existing('var_' + d + '_height'))
    );

  builder
    .name('var_papillary_heights')
    .bindRaw(
      Algo.makeValueMeasurement,
      DT.ValueMeasurement,
      papillaryHeightsInfo,
      builder.bindRaw(
        Algo.makeYesMaybeCriteria,
        DT.CheckCriteria,
        papillaryHeightsInfo,
        builder.bindRaw(Algo.makeGreaterThanCriteriaRange, DT.CriteriaRange, builder.define(new DT.NumberT(2)))
      )
    );

  builder
    .name('all_papillary_points')
    .bind(Algo.makePointsArray, DT.PointsArray, ...descriptors.map((d) => 'tp.pt_' + d));
}

export function buildLeafletCommonAlgorithm(builder: DataFlowBuilder, descriptors: Array<string>): void {
  builder.name('available_leaflet').define(new DT.IdentifiersList(descriptors));
}
